import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import profilePic from '../assets/profile.jpg';
import ContactModal from './ContactModal';
import UnifiedCard from './ui/UnifiedCard';

const edifiLogo = process.env.PUBLIC_URL + '/edifi_icon.png';
const ammopantryLogo = process.env.PUBLIC_URL + '/ammopantryai_icon.png';
const yapaiLogo = process.env.PUBLIC_URL + '/yapai_icon.svg';
const buildWithLimitsLogo = process.env.PUBLIC_URL + '/buildwithlimits_icon.png';

const products = [
    {
        id: 'buildwithlimits',
        logo: buildWithLimitsLogo,
        name: 'Build With Limits',
        tagline: 'Systems that hold',
        description: 'Health, fitness, and motherhood systems for ambitious women who don\'t have time to wait for ideal conditions. Programs, gear recommendations, and the playbook for building while raising kids.',
        status: 'Live',
        link: 'https://buildwithlimits.com',
    },
    {
        id: 'ammopantry',
        logo: ammopantryLogo,
        name: 'AmmoPantry AI',
        tagline: 'Smart inventory for iOS & Android',
        description: 'AI-powered inventory management for hunters, shooters, and preppers. Snap a photo of any ammo box and AI instantly identifies and logs it. Tracks firearms, reloading materials, and emergency supplies with family sharing.',
        status: 'Live on App Store & Play Store',
        link: 'https://ammopantryai.app',
    },
    {
        id: 'yapai',
        logo: yapaiLogo,
        name: 'Yap AI',
        tagline: 'Communication coaching',
        description: 'Record real conversations and get AI-powered feedback on how you communicate. Structured Stop/Keep/Start coaching with tonality scores, alternative phrasings, and long-term progress tracking.',
        status: 'Launching Soon',
        link: 'https://yapai.app',
    },
    {
        id: 'edifi',
        logo: edifiLogo,
        name: 'Edifi',
        tagline: 'Family Health OS',
        description: 'A family health operating system that replaces the clipboard. Enter your family\'s health profiles once and Edifi pre-fills intake forms, generates AI supplement recommendations, and hands off a clinical summary to any doctor via QR code.',
        status: 'YC S26 Application',
        link: null,
    },
];

const pillars = [
    {
        number: '01',
        title: 'AI for Real Life',
        description: 'I build AI products that solve real problems — not demos, not toys. Products that work when life doesn\'t cooperate.',
    },
    {
        number: '02',
        title: 'Fitness in the Margins',
        description: 'Systems that hold through hard seasons. The anti-influencer fitness approach for women who don\'t have hours to spare.',
    },
    {
        number: '03',
        title: 'The Both/And Life',
        description: 'Ambition and presence aren\'t competing. I\'m building the counter-narrative — you don\'t have to choose.',
    },
];

const HomePage = () => {
    const [contactOpen, setContactOpen] = useState(false);

    return (
        <HelmetProvider>
            <div className="flex-1 bg-cream text-ink">
                <Helmet prioritizeSeoTags>
                    <meta charSet="utf-8" />
                    <title>Amanda Filomena — Founder & AI Engineer</title>
                    <link rel="canonical" href="https://amandafilomena.com/" />
                    <meta name="description" content="Amanda Filomena — AI Engineer, founder, and mom building systems for ambitious women who don't have time to wait for ideal conditions." />
                    <script type="application/ld+json">{JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "Person",
                                "@id": "https://amandafilomena.com/#person",
                                "name": "Amanda Filomena Anderson",
                                "alternateName": "Amanda Filomena",
                                "url": "https://amandafilomena.com",
                                "image": "https://amandafilomena.com/profile.jpg",
                                "jobTitle": "AI Engineer & Founder",
                                "description": "AI Engineer, founder of Edifi, AmmoPantry AI, Yap AI, and Build With Limits. YC S26 applicant, based in Utah.",
                                "sameAs": [
                                    "https://www.linkedin.com/in/amanda-fc-anderson/",
                                    "https://github.com/amandavc-BYU",
                                    "https://www.instagram.com/amandafilomena_/"
                                ]
                            },
                            {
                                "@type": "WebSite",
                                "@id": "https://amandafilomena.com/#website",
                                "url": "https://amandafilomena.com",
                                "name": "Amanda Filomena",
                                "description": "AI Engineer, founder, and mom building systems for ambitious women.",
                                "author": { "@id": "https://amandafilomena.com/#person" }
                            }
                        ]
                    })}</script>
                </Helmet>

                {/* Hero */}
                <section className="border-b border-sand">
                    <div className="mx-auto max-w-6xl px-6 py-24 md:py-32">
                        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:items-center lg:gap-20">
                            {/* Text */}
                            <div className="opacity-0 animate-fade-up order-last lg:order-first">
                                <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-8">
                                    Mom · Engineer · Founder
                                </p>
                                <h1 className="text-4xl md:text-5xl lg:text-[3.2rem] leading-[1.15] tracking-tight text-ink mb-8" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>
                                    Building What I Wish Existed
                                </h1>
                                <p className="mb-12 text-lg leading-relaxed text-muted max-w-lg">
                                    For work, fitness, and life with littles. I create systems that actually hold when life doesn't cooperate.
                                </p>
                                <Link
                                    to="/work"
                                    className="group inline-flex items-center gap-3 text-sm font-medium tracking-wide text-ink border-b border-ink pb-2 transition-all hover:text-terra hover:border-terra"
                                >
                                    Explore What I'm Building
                                    <span className="transition-transform group-hover:translate-x-1">→</span>
                                </Link>
                            </div>

                            {/* Image */}
                            <div className="opacity-0 animate-fade-up-delay flex justify-center lg:justify-end order-first lg:order-last">
                                <div className="relative">
                                    <div className="aspect-[4/5] w-full max-w-[420px] overflow-hidden">
                                        <img
                                            src={profilePic}
                                            alt="Amanda Filomena — AI Engineer and Founder"
                                            className="h-full w-full object-cover"
                                            width={420}
                                            height={525}
                                            loading="eager"
                                        />
                                    </div>
                                    {/* Floating badge */}
                                    <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-surface/95 backdrop-blur-sm border border-sand px-5 py-4 shadow-lg">
                                        <p className="text-xs font-medium text-muted mb-1">Currently building</p>
                                        <p className="text-sm font-medium text-ink">Edifi · YapAI · AmmoPantry</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Products Section */}
                <section className="border-b border-sand py-20 md:py-28">
                    <div className="mx-auto max-w-6xl px-6">
                        <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                            <div>
                                <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-4">What I'm Building</p>
                                <h2 className="text-2xl md:text-3xl text-ink" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>Products & Ventures</h2>
                            </div>
                            <Link to="/work" className="text-xs font-medium uppercase tracking-wider text-muted border-b border-sand pb-1 hover:text-ink hover:border-ink transition-colors">
                                View All →
                            </Link>
                        </div>

                        {/* Horizontal scrolling products */}
                        <div className="relative w-full overflow-x-auto no-scrollbar -mx-6">
                            <div className="flex gap-4 px-6 pb-6">
                                {products.map((product) => (
                                    <UnifiedCard
                                        key={product.id}
                                        status={product.status}
                                        name={product.name}
                                        subtitle={product.tagline}
                                        description={product.description}
                                        link={product.link}
                                        imageUrl={product.logo}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Pillars Section */}
                <section className="border-b border-sand px-6 py-20 md:py-28">
                    <div className="mx-auto max-w-6xl">
                        <div className="mb-16">
                            <p className="text-xs font-medium tracking-[0.2em] text-terra uppercase mb-4">What I Believe</p>
                            <h2 className="text-2xl md:text-3xl text-ink" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>The Philosophy</h2>
                        </div>
                        <div className="grid grid-cols-1 gap-16 md:grid-cols-3 md:gap-12">
                            {pillars.map((pillar) => (
                                <div key={pillar.number}>
                                    <p className="text-5xl text-sand mb-6" style={{ fontFamily: "'Cinzel', serif" }}>{pillar.number}</p>
                                    <h3 className="text-base font-medium text-ink mb-3 tracking-wide" style={{ fontFamily: "'Cinzel', serif" }}>{pillar.title}</h3>
                                    <p className="text-sm text-muted leading-relaxed">{pillar.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Collaboration Banner */}
                <section className="bg-sand/50 px-6 py-12 md:py-16">
                    <div className="mx-auto max-w-6xl flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <h2 className="text-2xl md:text-3xl text-ink" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>
                            Looking to Collaborate?
                        </h2>
                        <button
                            onClick={() => setContactOpen(true)}
                            className="inline-flex items-center justify-center bg-ink px-8 py-4 text-xs font-medium uppercase tracking-wider text-cream transition-all hover:bg-terra"
                        >
                            Make an Inquiry
                        </button>
                    </div>
                </section>

                {/* Newsletter Section */}
                <section className="px-6 py-20 md:py-24">
                    <div className="mx-auto max-w-xl text-center">
                        <h2 className="text-2xl md:text-3xl text-ink mb-4" style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}>
                            Sign Up for the Build Log
                        </h2>
                        <p className="text-sm text-muted mb-8 max-w-md mx-auto leading-relaxed">
                            Get behind-the-scenes updates on what I'm building — in AI, fitness, and life with littles.
                        </p>

                        <a
                            href="https://amandafilomena.substack.com/welcome"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center bg-ink px-10 py-4 text-xs font-medium uppercase tracking-wider text-cream transition-all hover:bg-terra"
                        >
                            Subscribe on Substack
                        </a>
                    </div>
                </section>

                {/* Social Links */}
                <section className="bg-ink px-6 py-16">
                    <div className="mx-auto max-w-6xl flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <p className="text-sm text-cream/60">Follow along on social</p>
                        <div className="flex flex-wrap gap-4">
                            <a
                                href="https://www.instagram.com/amandafilomena_/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 border border-cream/20 px-6 py-3 text-xs font-medium uppercase tracking-wider text-cream transition-all hover:border-cream/50"
                            >
                                Instagram
                            </a>
                            <a
                                href="https://www.linkedin.com/in/amanda-fc-anderson/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 border border-cream/20 px-6 py-3 text-xs font-medium uppercase tracking-wider text-cream transition-all hover:border-cream/50"
                            >
                                LinkedIn
                            </a>
                            <a
                                href="https://github.com/amandafc-BYU"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 border border-cream/20 px-6 py-3 text-xs font-medium uppercase tracking-wider text-cream transition-all hover:border-cream/50"
                            >
                                GitHub
                            </a>
                        </div>
                    </div>
                </section>

                {/* Contact Modal */}
                <ContactModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />

                {/* Footer */}
                <footer className="border-t border-sand px-6 py-8">
                    <div className="mx-auto max-w-6xl flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <p className="text-sm text-muted">© 2026 Amanda Filomena</p>
                        <div className="flex gap-6">
                            <a href="https://github.com/amandafc-BYU" target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-wider text-muted hover:text-terra transition-colors">
                                GitHub
                            </a>
                            <a href="mailto:hello@amandafilomena.com" className="text-xs uppercase tracking-wider text-muted hover:text-terra transition-colors">
                                Contact
                            </a>
                        </div>
                    </div>
                </footer>
            </div>
        </HelmetProvider>
    );
};

export default HomePage;
